import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router'
import { push } from 'connected-react-router'
import { connect } from 'react-redux'
import { get } from 'lodash'
import { compose } from 'recompose'
import queryString from 'query-string'
import appConfig from 'config'

import { logError, modal, notification, setGlobalLoading, updateActiveTab } from 'redux/modules/app'
import {
  activities as activitiesApi,
  awards as awardsApi,
  participants as participantsApi,
  tasks as tasksApi
} from 'redux/modules/api'
import { createSchema, schemaLogs, updateSchema } from 'models/activities'
import { changeAssessorSchema } from 'models/assessors'
import { ACTIVITY_SECTIONS, PROGRESS_STATES } from 'constants/enums'
import { parseAuthCode } from 'utils/authCode'

function decorator(config = {}) {
  return ComposedComponent => {
    const enhance = compose(
        withRouter,
        connect(
            ({ api, auth }) => ({
              activities: api.activities,
              localTasks: api.tasks.sectionSignoff,
              user: auth.user,
              activeOrganizationId: auth.organization.id,
            }),
            {
              ...activitiesApi,
              notification,
              updateActiveTab,
              setGlobalLoading,
              logError,
              modal,
              push,
              getParticipant: participantsApi.getParticipant,
              getTasksSectionSignoff:
              tasksApi.sectionSignoff.getTasksSectionSignoff,
              getTasksActivityApproval:
              tasksApi.activityApproval.getTasksActivityApproval,
              getTasksOverview: tasksApi.overview.getTasksOverview,
              getAwards: awardsApi.getAwards
            }
        )
    )

    class Activities extends Component {
      static propTypes = {
        route: PropTypes.object,
        user: PropTypes.object.isRequired,
        activeOrganizationId: PropTypes.number.isRequired,
        params: PropTypes.object,
        location: PropTypes.object,
        activityId: PropTypes.number,
        localTasks: PropTypes.object.isRequired,
        getActivities: PropTypes.func.isRequired,
        getActivity: PropTypes.func.isRequired,
        getPublicActivity: PropTypes.func.isRequired,
        getActivitiesCategories: PropTypes.func.isRequired,
        getAjActivityTypes: PropTypes.func.isRequired,
        changeActivityAssessor: PropTypes.func.isRequired,
        updateActivity: PropTypes.func.isRequired,
        setupActivity: PropTypes.func.isRequired,
        approveActivity: PropTypes.func.isRequired,
        signoffActivity: PropTypes.func.isRequired,
        getParticipant: PropTypes.func.isRequired,
        getActivityLog: PropTypes.func.isRequired,
        createActivityLog: PropTypes.func.isRequired,
        createActivity: PropTypes.func.isRequired,
        removeActivityLog: PropTypes.func.isRequired,
        editActivityLog: PropTypes.func.isRequired,
        getActivityLogs: PropTypes.func.isRequired,
        getPublicActivityLogs: PropTypes.func.isRequired,
        getTasksSectionSignoff: PropTypes.func.isRequired,
        getTasksActivityApproval: PropTypes.func.isRequired,
        getTasksOverview: PropTypes.func.isRequired,
        getAwards: PropTypes.func.isRequired,
        assessorApproveActivity: PropTypes.func,
        notification: PropTypes.func.isRequired,
        push: PropTypes.func.isRequired,
        modal: PropTypes.func.isRequired,
        setGlobalLoading: PropTypes.func.isRequired,
        updateLists: PropTypes.func,
        sendActivityAssessorReport: PropTypes.func.isRequired,
        changeActivityState: PropTypes.func.isRequired,
        getActivityAssessment: PropTypes.func.isRequired,
        resendActivityAssessmentRequest: PropTypes.func.isRequired,
        getActivityAssessmentRequests: PropTypes.func.isRequired,
        sendAssessorWelcomeEmail: PropTypes.func.isRequired,
        getPublicActivityAssessmentRequests: PropTypes.func.isRequired,
        updateActiveTab: PropTypes.func.isRequired,
        groupedActivities: PropTypes.object,
        deleteActivity: PropTypes.func.isRequired,
        restoreActivityLog: PropTypes.func.isRequired,
        restoreActivity: PropTypes.func.isRequired,
        logError: PropTypes.func.isRequired,
        getActivityReport: PropTypes.func.isRequired
      }

      componentDidMount() {
        const { params, activityId } = this.props

        config.list && this.handleGetActivities()
        config.detail && this.handleGetActivity(params && params.activityId)
        config.publicDetail &&
        this.handleGetPublicActivity(params && params.activityId)
        config.activityCategories &&
        this.handleGetActivitiesCategories(config.template)
        config.ajTypes && this.handleGetAjActivityTypes()
        config.activityLogs &&
        activityId &&
        this.handleGetActivityLogs(activityId)
        config.publicActivityLogs && this.handleGetPublicActivityLogs()
      }

      canDeleteActivity = currentActivityId => {
        const activities = get(this.props, 'awards.list[0].activities', [])
        const currActivity = activities.find(
            item => currentActivityId === item.id
        )
        const activitiesInSection = activities.filter(
            activity =>
                get(activity, 'activitySection.value') ===
                get(currActivity, 'activitySection.value')
        )

        return (
            activitiesInSection.filter(
                activity =>
                    get(activity, 'activityState.value') !==
                    PROGRESS_STATES.activityDeleted.id
            ).length > 1
        )
      }

      handleGetActivities = () => this.props.getActivities()

      handleGetActivity = id => this.props.getActivity(id)

      handleGetPublicActivity = id => {
        const authCode = parseAuthCode(this.props)

        this.props.getPublicActivity(id, authCode).catch(this.handleError)
      }

      handleGetActivitiesCategories = (template, parentId) =>
          this.props.getActivitiesCategories(template, parentId)

      handleGetAjActivityTypes = ajType => {
        this.props.getAjActivityTypes(ajType)
      }

      handleGetActivityLogs = id => this.props.getActivityLogs(id)

      handleGetPublicActivityLogs = () => {
        const query = queryString.parse(get(this.props, 'location.search'))

        this.props.getPublicActivityLogs(
            get(query, 'authorizationCode')
        )
      }

      handleCreateActivity = (awardId, fields) => {
        const { groupedActivities } = this.props
        const section = get(fields, 'activitySection.value', 0)

        const sectionWithEmptyActivity =
            groupedActivities[section].length === 1 &&
            !get(groupedActivities[section], '[0].activityCategory')

        if (sectionWithEmptyActivity) {
          return Promise.reject(
              this.props.notification(
                  'Fill in the first activity before adding a new one.',
                  {
                    status: 'error',
                    intlKey: 'notifications.fill_first_activity_before_new',
                    autoDismiss: 7
                  }
              )
          )
        }

        return this.props
            .createActivity(awardId, createSchema(fields))
            .then(() =>
                this.handleSuccess(
                    'New activity has been added',
                    'notification.new_activity_added'
                )
            )
            .then(() => {
              const sectionKey = Object.keys(ACTIVITY_SECTIONS).find(
                  key => ACTIVITY_SECTIONS[key].id === section
              )
              const sectionIndex = get(ACTIVITY_SECTIONS[sectionKey], 'index')

              sectionIndex &&
              this.props.updateActiveTab('participantLevels', sectionIndex)

              if (groupedActivities) {
                const activity = groupedActivities[section]

                activity &&
                this.props.updateActiveTab('activities', activity.length)
              }
            })
            .catch(this.handleError)
      }

      handleSetupActivity = (id, fields) => {
        this.props
            .setupActivity(id, updateSchema(fields))
            .then(() =>
                this.handleSuccess(
                    'Activity has been set up',
                    'notifications.activity_has_been_setup'
                )
            )
            .catch(this.handleError)
      }

      handleUpdateActivity = (id, fields) =>
          this.props
              .updateActivity(id, updateSchema(fields))
              .then(() =>
                  this.handleSuccess(
                      'Activity has been updated',
                      'notifications.activity_has_been_updated'
                  )
              )
              .catch(this.handleError)

      handleApproveActivity = (activityId, fields, isAssessorManagementEnabled = false) =>
          this.props
              .approveActivity(activityId, {
                approved: true,
                preventSendEmailToAssessor: fields.preventSendEmailToAssessor
              })
              .then(() => {
                  if (isAssessorManagementEnabled) {
                      this.handleSuccess(
                          'Activity and Assessor have been authorised, Assessor has been sent invitation',
                          'notifications.assessor_and_activity_has_been_approved'
                      )
                  } else {
                      this.handleSuccess(
                          'Activity has been approved',
                          'notifications.activity_has_been_approved'
                      )
                  }
              })
              .catch(this.handleError)

      handleReturnActivity = (activityId, fields) =>
          this.props
              .approveActivity(activityId, { approved: false, note: fields.note, reason: fields.reason })
              .then(() =>
                  this.handleSuccess(
                      'Activity has been returned',
                      'notifications.activity_has_been_returned'
                  )
              )
              .catch(this.handleError)

      handleSignoffActivity = activityId =>
          this.props
              .signoffActivity(activityId, { approved: true })
              .then(() =>
                  this.handleSuccess(
                      'Section has been signed off',
                      'notifications.section_has_been_signed_off'
                  )
              )
              .catch(this.handleError)

      handleReturnSignoffActivity = (activityId, fields) =>
          this.props
              .signoffActivity(activityId, { approved: false, note: fields.note })
              .then(() =>
                  this.handleSuccess(
                      'Section has been returned',
                      'notifications.section_has_been_returned'
                  )
              )
              .catch(this.handleError)

      handleGetActivityLog = id => this.props.getActivityLog(id)

      handleRestoreActivityLog = id =>
          this.props
              .restoreActivityLog(id)
              .then(() =>
                  this.handleSuccess(
                      'Activity log has been restored',
                      'notification.log_has_been_restored'
                  )
              )

      handleCreateActivityLog = (activityId, fields) =>
          this.props
              .createActivityLog(schemaLogs(activityId, fields))
              .then(() =>
                  this.handleSuccess(
                      'New log has been added',
                      'notifications.new_log_has_been_added'
                  )
              )
              .catch(this.handleError)

      handleRemoveActivityLog = (logId, removeLogSet) => {
        return new Promise(res => {
          this.props
              .removeActivityLog(logId, { removeLogSet })
              .then(() => {
                this.handleSuccess(
                    'Log has been removed',
                    'notifications.log_has_been_removed'
                )
                res()
              })
              .catch(this.handleError)
        })
      }

      handleEditActivityLog = (logId, fields) =>
          this.props
              .editActivityLog(logId, schemaLogs(null, fields))
              .then(() =>
                  this.handleSuccess(
                      'Log has been modified',
                      'notifications.log_has_been_modified'
                  )
              )
              .catch(this.handleError)

      handleAssessorApprove = () => {
        const { params } = this.props

        this.props
            .assessorApproveActivity(params && params.activityId, {
              approved: true
            })
            .then(this.handleSuccess)
            .catch(this.handleError)
      }

      handleApproveActivityAssessorReport = data => {
       this.props
            .sendActivityAssessorReport(
                get(this.props, 'params.activityId'),
                parseAuthCode(this.props),
              data
            )
            .then(() => {
              this.props.push(`${appConfig.publicPath}/assessor-assessment/success`)
              this.handleSuccess()
            })
            .catch(this.handleError)
      }

      handleRejectActivityAssessorReport = fields => {
        this.props
            .sendActivityAssessorReport(
                get(this.props, 'params.activityId'),
                parseAuthCode(this.props),
                {
                  note: fields.note,
                  approved: false,
                  assessorReportDetail: {}
                }
            )
            .then(this.handleSuccess)
            .catch(this.handleError)
      }

      handleChangeActivityState = (id, state) => {
        if (state === PROGRESS_STATES.activityDeleted.id) {
          if (this.canDeleteActivity(id)) {
            return this.handleDeleteActivity(id)
          }
          this.props.modal()

          return this.props.notification(
              'One activity in section has to remain undeleted.',
              {
                status: 'error',
                intlKey: 'notifications.one_activity_has_to_remain'
              }
          )
        }

        this.props.setGlobalLoading(true)

        this.props
            .changeActivityState(id, state)
            .then(this.handleSuccess)
            .catch(this.handleError)
      }

      handleDeleteActivity = id =>
          this.props
              .deleteActivity(id)
              .then(this.handleSuccess)
              .catch(this.handleError)

      handleRestoreActivity = id =>
          this.props
              .restoreActivity(id)
              .then(this.handleSuccess)
              .catch(this.handleError)

      handleGetActivityAssessment = (activityId, activityActionId) => {
        this.props
            .getActivityAssessment(activityId, activityActionId)
            .then(() => {
            })
            .catch(this.handleError)
      }

      handleGetActivityAssessmentRequests = (activityId, activityActionId) => {
        this.props
            .getActivityAssessmentRequests(activityId, activityActionId)
            .then(() => {
            })
            .catch(this.handleError)
      }

      handleResendActivityAssessmentRequest = id => {
        this.props
            .resendActivityAssessmentRequest(id)
            .then(() =>
                this.handleSuccess(
                    'Assessment request has been sent',
                    'notifications.assessment_request_has_been_sent'
                )
            )
            .catch(this.handleError)
      }

      handleSendActivityAssessorWelcomeEmail = id => {
        this.props
            .sendAssessorWelcomeEmail(id)
            .then(() =>
                this.handleSuccess(
                    'Assessor welcome email has been sent',
                    'notifications.assessment_welcome_email_has_been_sent'
                )
            )
            .catch(this.handleError)
      }

      handleGetPublicActivityAssessmentRequests = (
          activityId,
          activityActionId
      ) => {
        this.props
            .getPublicActivityAssessmentRequests(
                activityId,
                activityActionId,
                parseAuthCode(this.props)
            )
            .then(() => {
            })
            .catch(this.handleError)
      }

      handleChangeActivityAssessor = fields =>
          this.props
              .changeActivityAssessor(fields.id, changeAssessorSchema(fields))
              .then(() => {
                this.handleSuccess(
                    'Assessor has been updated',
                    'notifications.assessor_has_been_updated'
                )
              })
              .catch(this.handleError)

      handleSuccess = (
          text = 'Activity updated',
          intlKey = 'notifications.activity_has_been_updated'
      ) => {
        this.props.notification(text, {
          status: 'success',
          intlKey
        })
        this.props.modal(null)
        this.handleRefresh()
        this.props.setGlobalLoading(false)
      }

      handleError = err => {
        this.props.logError(err, {
          authCode: parseAuthCode(this.props)
        })
        this.props.setGlobalLoading(false)
      }

      handleRefresh = () => {
        const { localTasks, activityId, route } = this.props

        const awardId = get(this.props, 'match.params.id')

        config.updateParticipantDetails &&
        this.props.getParticipant(awardId)
        config.updateSectionTasks &&
        this.props.getTasksSectionSignoff(localTasks).catch(this.handleError)

        if (config.updateActivityTasks) {
          if (this.props.updateLists) {
            this.props.updateLists()
          } else {
            this.props
                .getTasksActivityApproval(localTasks)
                .catch(this.handleError)
          }
        }

        if (config.updateActivityTasks || config.updateSectionTasks) {
          this.props.getTasksOverview(null, {
            activeOrganizationId: this.props.activeOrganizationId,
            awardLeaderId: this.props.user.personId
          })
        }

        if (config.updateLogs && activityId) {
          this.handleGetActivityLogs(activityId)
        }

        if (config.updateParticipantAward) {
          get(route, 'awardLevelId') &&
          this.props.getAwards(
              undefined,
              `participant.id=${awardId},awardLevel.value=${
                  route.awardLevelId
              }`,
              'PAYMENT_INFO,LEADER,PARTICIPANT_REGISTRATION'
          )
          activityId && this.handleGetActivityLogs(activityId)
        }
      }

        handleGetActivityReport = activityId => {
          return this.props.getActivityReport(activityId)
        }

      render() {
        return (
            <ComposedComponent
                {...this.props}
                updateActivity={this.handleUpdateActivity}
                setupActivity={this.handleSetupActivity}
                approveActivity={this.handleApproveActivity}
                returnActivity={this.handleReturnActivity}
                signoffActivity={this.handleSignoffActivity}
                getActivityLog={this.handleGetActivityLog}
                returnSignoffActivity={this.handleReturnSignoffActivity}
                onCreateActivity={this.handleCreateActivity}
                onRestoreActivity={this.handleRestoreActivity}
                onActivityParentChange={({ value }) =>
                    this.handleGetActivitiesCategories(null, value)
                }
                onAjTypeChange={({ value }) =>
                    this.handleGetAjActivityTypes(value)
                }
                onRestoreActivityLog={this.handleRestoreActivityLog}
                createActivityLog={this.handleCreateActivityLog}
                removeActivityLog={this.handleRemoveActivityLog}
                editActivityLog={this.handleEditActivityLog}
                assessorApproveActivity={this.handleAssessorApprove}
                approveActivityAssessorReport={
                  this.handleApproveActivityAssessorReport
                }
                rejectActivityAssessorReport={
                  this.handleRejectActivityAssessorReport
                }
                changeActivityState={this.handleChangeActivityState}
                getActivityAssessment={this.handleGetActivityAssessment}
                resendActivityAssessmentRequest={
                  this.handleResendActivityAssessmentRequest
                }
                sendAssessorWelcomeEmail={
                  this.handleSendActivityAssessorWelcomeEmail
                }
                getActivityAssessmentRequests={
                  this.handleGetActivityAssessmentRequests
                }
                getPublicActivityAssessmentRequests={
                  this.handleGetPublicActivityAssessmentRequests
                }
                changeActivityAssessor={this.handleChangeActivityAssessor}
                localTasks={undefined}
                getActivityReport={this.handleGetActivityReport}
            />
        )
      }
    }

    return enhance(Activities)
  }
}

export default decorator
